// src/components/admin/CreateTryoutForm.js
import { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function CreateTryoutForm({ existingTryout }) {
  const navigate = useNavigate();
  const { teamId } = useParams(); // Get teamId from the URL

  // Helper to extract HH:MM from a full timestamp string
  const extractTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const [isLoading, setIsLoading] = useState(!existingTryout);
  
  // Initialize form state
  const [form, setForm] = useState({
    program_id: existingTryout?.program_id || "",
    team_id: existingTryout?.team_id || teamId || "",
    tryout_date: existingTryout?.tryout_date || "",
    start_time: extractTime(existingTryout?.start_time) || "",
    end_time: extractTime(existingTryout?.end_time) || "",
    location: existingTryout?.location || "",
    description: existingTryout?.description || "",
    grade_offerings: existingTryout?.grade_offerings?.join(", ") || "",
    gender: existingTryout?.gender || "",
  });

  // Fetch the Program ID associated with this Team if creating new
  useEffect(() => {
    const fetchProgramId = async () => {
      if (existingTryout) {
        setIsLoading(false);
        return; 
      }

      try {
        const { data, error } = await supabase
          .from("teams")
          .select("program_id")
          .eq("id", teamId)
          .single();

        if (error) throw error;

        if (data) {
          setForm((prev) => ({ ...prev, program_id: data.program_id }));
        }
      } catch (err) {
        console.error("Error fetching program ID:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (teamId) {
      fetchProgramId();
    }
  }, [teamId, existingTryout]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.program_id || !form.team_id) {
      alert("Missing Program or Team ID. Please try refreshing.");
      return;
    }

    // 1. Create JS Date objects (Browser assumes Local Time)
    const startDateObj = new Date(`${form.tryout_date}T${form.start_time}`);
    const endDateObj = new Date(`${form.tryout_date}T${form.end_time}`);

    // 2. Check validity
    if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
      alert("Invalid date or time entered.");
      return;
    }

    // 3. Convert to UTC ISO Strings for Supabase (timestamptz)
    const fullStartTime = startDateObj.toISOString();
    const fullEndTime = endDateObj.toISOString();

    const grades = form.grade_offerings.split(",").map((g) => g.trim()).filter(g => g !== "");

    const payload = {
      program_id: form.program_id,
      team_id: form.team_id,
      tryout_date: form.tryout_date,
      start_time: fullStartTime,
      end_time: fullEndTime,
      location: form.location,
      description: form.description,
      grade_offerings: grades,
      gender: form.gender,
    };

    let error;

    if (existingTryout) {
      // Update
      const { error: updateError } = await supabase
        .from("tryouts")
        .update(payload)
        .eq("id", existingTryout.id);
      error = updateError;
    } else {
      // Insert
      const { error: insertError } = await supabase
        .from("tryouts")
        .insert([payload]);
      error = insertError;
    }

    if (error) {
      console.error("Error saving tryout:", error);
      alert(`Error: ${error.message}`);
    } else {
      // --- CORRECTED REDIRECT PATH ---
      navigate(`/admin/program/${form.program_id}/team/${form.team_id}/tryouts`);
    }
  };

  if (isLoading) return <p className="p-6 text-gray-500">Loading team details...</p>;

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded shadow">
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)} 
        className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-6"
      >
        <ArrowLeft size={20} />
        <span>Back</span>
      </button>

      <h2 className="text-2xl font-semibold mb-6">
        {existingTryout ? "Edit Tryout" : "Create New Tryout"}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-5">
        
        {/* Date Field */}
        <div>
          <label className="block mb-1 font-medium text-gray-700">Tryout Date</label>
          <input
            type="date"
            name="tryout_date"
            value={form.tryout_date}
            onChange={handleChange}
            className="w-full border border-gray-300 px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
            required
          />
        </div>

        {/* Time Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1 font-medium text-gray-700">Start Time</label>
            <input
              type="time" 
              name="start_time"
              value={form.start_time}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
          </div>

          <div>
            <label className="block mb-1 font-medium text-gray-700">End Time</label>
            <input
              type="time"
              name="end_time"
              value={form.end_time}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
          </div>
        </div>

        {/* Location */}
        <div>
          <label className="block mb-1 font-medium text-gray-700">Location</label>
          <input
            name="location"
            value={form.location}
            onChange={handleChange}
            placeholder="e.g. Main High School Gym"
            className="w-full border border-gray-300 px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="block mb-1 font-medium text-gray-700">Description</label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="Additional details for athletes..."
            rows={3}
            className="w-full border border-gray-300 px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>

        {/* Grades */}
        <div>
          <label className="block mb-1 font-medium text-gray-700">Grades (comma-separated)</label>
          <input
            name="grade_offerings"
            value={form.grade_offerings}
            onChange={handleChange}
            placeholder="e.g. 9, 10, 11, 12"
            className="w-full border border-gray-300 px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>

        {/* Gender */}
        <div>
          <label className="block mb-1 font-medium text-gray-700">Gender</label>
          <select
            name="gender"
            value={form.gender}
            onChange={handleChange}
            className="w-full border border-gray-300 px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
            required
          >
            <option value="">Select Gender</option>
            <option value="Girls">Girls</option>
            <option value="Boys">Boys</option>
            <option value="Coed">Coed</option>
          </select>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            className="w-full md:w-auto px-8 py-3 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition shadow-sm"
          >
            {existingTryout ? "Update Tryout" : "Save Tryout"}
          </button>
        </div>

      </form>
    </div>
  );
}